import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { HardHat, Phone, CheckCircle2, ChevronDown, Loader2, X, Clock, Users, Ruler, MessageSquare, Languages } from "lucide-react";
import { API, SITE } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, RoadDash } from "@/components/bits";

const HERO_IMG = "/work/site-prep.jpg";

function ApplicationForm({ jobs }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", years_experience: "", message: "" });
  const [cv, setCv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.trim().length < 7 || !form.email.includes("@")) {
      setError("Please add your name, phone number and a valid email.");
      return;
    }
    if (!form.position) {
      setError("Please choose the position you're applying for.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/applications`, fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong — please try again or call us.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-joell-yellow/40 bg-joell-surface p-8 text-center sm:p-12" data-testid="application-success-panel">
        <CheckCircle2 size={48} className="mx-auto text-joell-yellow" />
        <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white">Thanks for applying.</h3>
        <p className="mt-3 text-slate-300">We've received your application and will be in touch.</p>
        <a href={SITE.phoneHref} className="btn-outline mt-8" data-testid="application-success-call">Call {SITE.phone}</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-white/10 bg-joell-surface p-6 sm:p-10" data-testid="application-form" noValidate>
      <h3 className="font-display text-2xl font-bold uppercase text-white">Apply now</h3>
      <p className="mt-2 text-sm text-slate-400">Fill in your details below — attach a CV if you have one.</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="af-name" className="field-label">Full Name *</label>
          <input id="af-name" className="input-dark" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="application-input-name" />
        </div>
        <div>
          <label htmlFor="af-phone" className="field-label">Phone *</label>
          <input id="af-phone" type="tel" className="input-dark" placeholder="441-..." value={form.phone} onChange={set("phone")} data-testid="application-input-phone" />
        </div>
        <div>
          <label htmlFor="af-email" className="field-label">Email *</label>
          <input id="af-email" type="email" className="input-dark" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="application-input-email" />
        </div>
        <div>
          <label htmlFor="af-position" className="field-label">Position *</label>
          <div className="relative">
            <select id="af-position" className="input-dark appearance-none pr-10" value={form.position} onChange={set("position")} data-testid="application-position-select">
              <option value="">Select a position…</option>
              {jobs.map((j) => <option key={j.id} value={j.title}>{j.title}</option>)}
            </select>
            <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
          </div>
        </div>
        <div>
          <label htmlFor="af-years" className="field-label">Years of asphalt experience</label>
          <input id="af-years" type="number" min="0" max="70" className="input-dark" placeholder="e.g. 12" value={form.years_experience} onChange={set("years_experience")} data-testid="application-input-years" />
        </div>
        <div>
          <label htmlFor="af-cv" className="field-label">CV / Resume (PDF, optional)</label>
          <button type="button" onClick={() => fileRef.current?.click()} className="input-dark flex items-center justify-between text-left text-sm" data-testid="application-cv-button">
            <span className={cv ? "text-joell-yellow" : "text-slate-500"}>{cv ? cv.name : "Attach CV (PDF)"}</span>
            {cv && <X size={14} className="text-slate-400" onClick={(e) => { e.stopPropagation(); setCv(null); if (fileRef.current) fileRef.current.value = ""; }} />}
          </button>
          <input ref={fileRef} id="af-cv" type="file" accept="application/pdf" onChange={(e) => setCv(e.target.files?.[0] || null)} className="hidden" data-testid="application-cv-input" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="af-message" className="field-label">Tell Us About Your Experience</label>
          <textarea id="af-message" rows={4} className="input-dark resize-y" placeholder="Machinery you've operated, projects you've worked on…" value={form.message} onChange={set("message")} data-testid="application-input-message" />
        </div>
      </div>
      {error && <p className="mt-5 border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="application-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-accent mt-8 w-full !py-4 disabled:opacity-60" data-testid="application-submit-button">
        {busy ? <Loader2 size={16} className="animate-spin" /> : "Submit Application"}
      </button>
      <p className="mt-4 text-center text-xs text-slate-500">
        Prefer to talk? Call <a href={SITE.phoneHref} className="text-joell-yellow">{SITE.phone}</a>.
      </p>
    </form>
  );
}

export default function Careers() {
  usePageTitle("Careers");
  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => (await axios.get(`${API}/jobs`)).data,
  });
  const job = jobs.find((j) => j.active) || jobs[0];

  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="careers-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/75 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-20 pt-36 md:pb-28 md:pt-48">
          <Reveal>
            <Eyebrow>Join the team</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              Careers at JOELL
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              We're always looking for hard-working people who take pride in the work. Apply below and tell us
              about your experience.
            </p>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-20 md:py-28" data-testid="careers-listing-section">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
          <div>
            <SectionHead
              eyebrow="Open position"
              title={job ? job.title : "No open roles right now"}
              className="!mb-8"
            />
            {job && (
              <Reveal delay={0.06}>
                <div className="border border-white/10 bg-joell-surface p-7 sm:p-9" data-testid="job-listing-card">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="bg-joell-yellow/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-joell-yellow" data-testid="job-openings">{job.openings} position{job.openings > 1 ? "s" : ""} available</span>
                    <span className="border border-white/15 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">{job.job_type}</span>
                  </div>
                  {job.summary && <p className="mt-5 text-sm leading-relaxed text-slate-300 sm:text-base">{job.summary}</p>}
                  {job.responsibilities?.length > 0 && (
                    <div className="mt-7">
                      <h4 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">The work</h4>
                      <ul className="mt-3 space-y-2.5">
                        {job.responsibilities.map((r) => (
                          <li key={r} className="flex items-start gap-3 text-sm text-slate-300" data-testid="job-responsibility">
                            <span aria-hidden="true" className="mt-1 inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {job.requirements?.length > 0 && (
                    <div className="mt-7">
                      <h4 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">What you'll need</h4>
                      <ul className="mt-3 space-y-2.5">
                        {job.requirements.map((r) => (
                          <li key={r} className="flex items-start gap-3 text-sm text-slate-300" data-testid="job-requirement">
                            <span aria-hidden="true" className="mt-1 inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Reveal>
            )}
            <Reveal delay={0.1}>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {[
                  [HardHat, "Real work, island-wide"],
                  [Clock, "Full-time positions"],
                  [Users, "Guidance for skilled laborers & apprentices"],
                  [Languages, "English communication"],
                ].map(([Icon, label], i) => (
                  <div key={label} className="flex items-center gap-3 border border-white/10 bg-joell-black px-4 py-3.5" data-testid={`careers-highlight-${i}`}>
                    <Icon size={17} className="shrink-0 text-joell-yellow" />
                    <span className="font-display text-sm font-semibold uppercase tracking-wide text-white">{label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.08}>
            <ApplicationForm jobs={jobs} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
